import React, { useCallback } from 'react';

import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { Box, Divider, Fab, Hidden, Typography } from '@mui/material';
import GridItem from '@jumbo/components/GridItem';
import GridContainer from '@jumbo/components/GridContainer';
import { Add, Delete, Remove } from '@mui/icons-material';
import ThemedTooltip from '@acnb/CustomComponents/Tooltip/ThemedTooltip';
import useImage from 'services/Hooks/useImage';
import { usePostChangeCartItemQuantity } from 'services/Data/CartService';

const useStyles = makeStyles(() => ({
  pageFull: {
    width: '100%',
  },
  center: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  deleteBtn: {
    position: 'absolute',
    right: '0',
    top: '0',
  },
  squareFab: {
    borderRadius: 0,
    width: 36,
    height: 36,
    minHeight: 36,
  },
}));

const CartItem = ({ item, handleOpenDeleteItemModal, index }) => {
  const classes = useStyles();
  const { getInventoryItemImage } = useImage();

  const { t } = useTranslation();
  const { mutate: changeQuantity } = usePostChangeCartItemQuantity();

  const handleQuantityChange = useCallback(
    quantity => {
      if (quantity <= 0) {
        handleOpenDeleteItemModal(item);
      } else {
        changeQuantity({ ...item, quantity });
      }
    },
    [changeQuantity, item, handleOpenDeleteItemModal],
  );

  const promoLimitReached = item.promotionQuantity > 0 && item.quantity >= item.promotionQuantity;

  return (
    <GridContainer>
      <GridItem
        xs={12}
        md={3}
        sx={{ alignSelf: 'center', p: '0 !important', justifyContent: 'center', alignItems: 'center' }}>
        <Box sx={{ p: 4 }}>
          <Box
            sx={{
              display: 'flex',
              alignContent: 'center',
              flexDirection: 'row',
              justifyContent: 'space-evenly',
              height: '100%',
            }}>
            <GridItem xs={12} md={3} sx={{ alignSelf: 'center' }}>
              <Box sx={{ textAlign: 'center' }}>
                <ThemedTooltip key={`item-${item.name}`} title={t('removeFromCart')} placement={'top'}>
                  <Fab
                    color="error"
                    size="small"
                    className={classes.squareFab}
                    onClick={() => {
                      handleOpenDeleteItemModal(item);
                    }}>
                    <Delete />
                  </Fab>
                </ThemedTooltip>
              </Box>
            </GridItem>
            <GridItem md={9} sx={{ alignSelf: 'center' }}>
              <Box className={classes.center}>
                <img
                  src={item.imgSrc ? getInventoryItemImage(item?.inventoryItem_Id, item?.imgSrc.id) : '/images/brp-logo.png'}
                  alt="img"
                  style={{ maxWidth: '20vh', width: '100%' }}
                />
              </Box>
            </GridItem>
          </Box>
        </Box>
      </GridItem>
      <GridItem
        xs={12}
        md={5}
        lg={4}
        sx={{ alignSelf: 'center', p: '0 !important', justifyContent: 'center', alignItems: 'center' }}>
        <Box sx={{ p: 4 }}>
          <Typography variant="h1">{item?.name}</Typography>
          <Typography variant="h5" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
            {item?.sku}
          </Typography>

          <Hidden mdDown>
            <Divider sx={{ mt: 0, mb: item.unitPricePromotion !== item.unitPrice ? 2 : 0 }} />
          </Hidden>

          <Hidden mdUp>
            <Divider sx={{ my: 3 }} />
          </Hidden>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
            <Box>
              <Box>
                <Typography variant="h2">{t('unitPrice')}</Typography>
              </Box>
              <Box>
                {item.quantityPerPackage > 0 && (
                  <Typography fontSize={14} sx={{ mt: 1, mb: 1, fontWeight: 'bold' }}>
                    {t('quantityPerPackageInfo', { quantityPerPackage: item.quantityPerPackage })}
                  </Typography>
                )}
              </Box>
            </Box>
            <Box>
              {item.unitPricePromotion !== item.unitPrice && (
                <Typography fontSize={16} color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                  ${item.unitPrice.toFixed(2)} {item.currency}
                </Typography>
              )}
              <Typography
                fontSize={25}
                color={item.unitPricePromotion !== item.unitPrice ? 'error' : 'text.primary'}
                fontWeight="bold">
                ${item.unitPricePromotion.toFixed(2)} {item?.currency}
              </Typography>
            </Box>
          </Box>
          {item?.bundledItems?.length > 0 && <Divider sx={{ mt: 0, mb: 2 }} />}
          {item?.bundledItems?.map(item => (
            <Typography fontSize={15} color="text.secondary" sx={{ mt: 1, mb: 1 }}>
              {`${item?.quantity}X ${item?.name || item?.sku}`}
            </Typography>
          ))}
        </Box>
      </GridItem>
      <Hidden mdDown>
        <GridItem
          md={2}
          lg={2}
          sx={{ alignSelf: 'center', p: '0 !important', justifyContent: 'center', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Box
              fontSize={20}
              display="flex"
              justifyContent={'space-between'}
              alignItems={'center'}
              sx={{ width: '100%', maxWidth: '10vw' }}>
              <Fab
                size="small"
                color="secondary"
                className={classes.squareFab}
                disabled={item.quantity <= 0}
                onClick={() => handleQuantityChange(item.quantity - 1)}>
                <Remove />
              </Fab>
              <Typography>{item.quantity}</Typography>
              <Fab
                size="small"
                color="secondary"
                className={classes.squareFab}
                sx={{
                  color: '#000',
                }}
                disabled={promoLimitReached}
                onClick={() => handleQuantityChange(item.quantity + 1)}>
                <Add />
              </Fab>
            </Box>
          </Box>
        </GridItem>
      </Hidden>

      <GridItem xs={12} md={2} lg={3} sx={{ alignSelf: 'center', p: '0 !important' }}>
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', height: '100%' }}>
          <Hidden mdDown implementation="css">
            {item.unitPricePromotion !== item.unitPrice && (
              <Typography variant="h3" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                ${(item?.unitPrice * item.quantity).toFixed(2)}
              </Typography>
            )}
            <Typography variant="h1" color={item.unitPricePromotion !== item.unitPrice ? 'error' : 'text.primary'}>
              ${item?.subTotal.toFixed(2)} {item.currency}
            </Typography>
          </Hidden>
        </Box>
      </GridItem>
      <Hidden mdUp implementation="js" style={{ width: '100%' }}>
        <GridItem xs={12}>
          <Divider />
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <Box
              fontSize={20}
              display="flex"
              justifyContent={'space-evenly'}
              alignItems={'center'}
              sx={{ mt: 4, mb: 5, width: '100%' }}>
              <Fab
                size="small"
                color="secondary"
                className={classes.squareFab}
                sx={{
                  color: '#000',
                }}
                disabled={item.quantity <= 0}
                onClick={() => handleQuantityChange(item.quantity - 1)}>
                <Remove />
              </Fab>
              <Typography>{item.quantity}</Typography>
              <Fab
                size="small"
                color="secondary"
                className={classes.squareFab}
                sx={{
                  color: '#000',
                }}
                disabled={promoLimitReached}
                onClick={() => handleQuantityChange(item.quantity + 1)}>
                <Add />
              </Fab>
            </Box>
          </Box>
          <Divider />
          <Box
            sx={{
              alignSelf: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexDirection: 'row',
              width: '100%',
              my: 3,
            }}>
            <Box fontSize={20} display="flex" justifyContent={'center'} alignItems={'center'}>
              <Box>
                <Typography variant="h3">{t('price')}</Typography>
              </Box>
            </Box>
            <Box display="flex" justifyContent={'center'} alignItems={'center'}>
              {item.unitPricePromotion !== item.unitPrice && (
                <Typography variant="h3" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                  ${(item?.unitPrice * item.quantity).toFixed(2)}
                </Typography>
              )}
              <Typography variant="h1" color={item.unitPricePromotion !== item.unitPrice ? 'error' : 'text.primary'}>
                ${item?.subTotal.toFixed(2)} {item.currency}
              </Typography>
            </Box>
          </Box>
        </GridItem>
      </Hidden>
    </GridContainer>
  );
};

export default CartItem;
